/**
 * Shared sketch core — the drawing engine behind every trade sketch pad
 * (conduit, duct, framing).  Pure geometry + types, no React, fully offline.
 *
 * Coordinates are FEET in plan view: x = right/east, y = down/south.
 */

export interface Pt {
  x: number;
  y: number;
}

/** A continuous run: a polyline of vertices drawn as one piece. */
export interface SketchRun {
  id: string;
  /** catalog key of the straight material (adapter specific) */
  kind: string;
  pts: Pt[];
  /** short mark, e.g. C-01 */
  mark: string;
  /** optional zone / area / circuit grouping */
  zone?: string;
  /** trade specific run options (conductors, liner, camber…) */
  meta?: Record<string, string | number | boolean>;
}

/** A point item placed on the plan — device, diffuser, base plate… */
export interface SketchMarker {
  id: string;
  kind: string;
  at: Pt;
  mark: string;
  zone?: string;
}

export interface SketchDoc {
  runs: SketchRun[];
  markers: SketchMarker[];
}

export interface SavedSketch {
  id: string;
  name: string;
  jobId?: string;
  savedAt: number;
  doc: SketchDoc;
}

/** One priced row of the takeoff. */
export interface TakeoffLine {
  /** unique row key */
  key: string;
  /** catalog quantity key handed to the job estimate */
  qtyKey: string;
  label: string;
  size: string;
  unit: string;
  qty: number;
  unitCost: number;
  hours: number;
  /** run marks this row came from */
  from?: string[];
  /** shown in red when set (fill over 40 %, span too long…) */
  warn?: string;
  /** takeoff section this row belongs to — matches the trade estimate sections */
  section?: string;
}

/** Drawing symbol used for a placed item on the canvas. */
export type SketchSymbol =
  | "square"
  | "panel"
  | "receptacle"
  | "switch"
  | "light"
  | "disconnect"
  | "transformer"
  | "ground"
  | "box";

export interface PaletteItem {
  key: string;
  label: string;
  short: string;
  size?: string;
  /** optional heading the item is listed under in the picker */
  group?: string;
}

export interface RunOptionField {
  id: string;
  label: string;
  /** select options */
  options: Array<{ value: string; label: string }>;
  default: string;
}

/** Everything that makes one trade's sketch pad different from another's. */
export interface TradeSketchAdapter {
  id: string;
  title: string;
  subtitle: string;
  /** localStorage key root */
  storageKey: string;
  /** localStorage key holding the estimate quantity map for this trade */
  estimateKey: string;
  estimateLabel: string;
  /** run mark prefix, e.g. "C" → C-01 */
  markPrefix: string;
  markerPrefix: string;
  runLabel: string;
  markerLabel: string;
  runItems: PaletteItem[];
  markerItems: PaletteItem[];
  /** per-run dropdowns shown in the toolbar (conductors, liner…) */
  runOptions?: RunOptionField[];
  /** stroke width in px for a run of this kind */
  strokeFor(kind: string): number;
  colorFor(kind: string): string;
  /** priced takeoff for the whole sketch */
  takeoff(doc: SketchDoc): TakeoffLine[];
  /** short one line summary under the canvas */
  summary(doc: SketchDoc): string;
  /** takeoff section headings, in order */
  sections?: Array<{ id: string; label: string }>;
  /** drawing symbol for a placed item */
  symbolFor?(kind: string): SketchSymbol;
  /** small tag drawn on the run in the canvas (size, conductors, fill…) */
  runBadge?(run: SketchRun): { text: string; warn?: boolean } | null;
  /** draw a home-run arrow at the start of the run */
  runIsHomerun?(run: SketchRun): boolean;
  /** true when this kind is drawn as a closed shape (a concrete pour, a room) */
  isClosed?(kind: string): boolean;
  /** fill colour for closed shapes */
  fillFor?(kind: string): string;
  /** dashed stroke for this kind (rebar, hidden work) */
  dashFor?(kind: string): string | undefined;
}

/* ------------------------------------------------------------------ */
/* geometry                                                            */
/* ------------------------------------------------------------------ */

export const SNAP_FT = 0.5;

export const snapVal = (v: number, step = SNAP_FT) => Math.round(v / step) * step;

export const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y);

/** Snap a free point onto the nearest 45° axis from `from`. */
export function snapPoint(from: Pt, to: Pt, allow45 = true): Pt {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const diag = allow45 && Math.abs(ax - ay) < Math.max(ax, ay) * 0.35;
  if (diag) {
    const d = snapVal((ax + ay) / 2);
    return { x: from.x + Math.sign(dx) * d, y: from.y + Math.sign(dy) * d };
  }
  if (ax >= ay) return { x: snapVal(to.x), y: from.y };
  return { x: from.x, y: snapVal(to.y) };
}

/** Turn angle in degrees at vertex b (0 = straight through). */
export function turnAngle(a: Pt, b: Pt, c: Pt): number {
  const a1 = Math.atan2(b.y - a.y, b.x - a.x);
  const a2 = Math.atan2(c.y - b.y, c.x - b.x);
  let d = ((a2 - a1) * 180) / Math.PI;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return Math.abs(d);
}

/** Total length of a run, feet. */
export const runLength = (run: SketchRun) =>
  run.pts.reduce((sum, p, i) => (i === 0 ? 0 : sum + dist(run.pts[i - 1], p)), 0);

/** Turns in a run, bucketed to the nearest standard fitting angle. */
export function runTurns(run: SketchRun): Array<{ at: Pt; angle: number }> {
  const out: Array<{ at: Pt; angle: number }> = [];
  for (let i = 1; i < run.pts.length - 1; i++) {
    const a = turnAngle(run.pts[i - 1], run.pts[i], run.pts[i + 1]);
    if (a < 5) continue;
    out.push({ at: run.pts[i], angle: a >= 67.5 ? 90 : 45 });
  }
  return out;
}

/** Feet as 12'-6 1/2" */
export function fmtFt(ft: number): string {
  const neg = ft < 0;
  const total = Math.abs(ft) * 12;
  const whole = Math.floor(total / 12);
  const inches = total - whole * 12;
  const sixteenths = Math.round(inches * 16);
  let inWhole = Math.floor(sixteenths / 16);
  let frac = sixteenths - inWhole * 16;
  let feet = whole;
  if (inWhole >= 12) {
    feet += 1;
    inWhole -= 12;
  }
  const g = (a: number, b: number): number => (b ? g(b, a % b) : a);
  let fracTxt = "";
  if (frac > 0) {
    const d = g(frac, 16);
    fracTxt = ` ${frac / d}/${16 / d}`;
  }
  return `${neg ? "-" : ""}${feet}'-${inWhole}${fracTxt}"`;
}

/** Enclosed area of a closed run, square feet (shoelace). */
export function runArea(run: SketchRun): number {
  const p = run.pts;
  if (p.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < p.length; i++) {
    const q = p[(i + 1) % p.length];
    a += p[i].x * q.y - q.x * p[i].y;
  }
  return Math.abs(a) / 2;
}

/** Perimeter of a closed run, feet. */
export function runPerimeter(run: SketchRun): number {
  const p = run.pts;
  if (p.length < 3) return runLength(run);
  return runLength(run) + dist(p[p.length - 1], p[0]);
}

export function docBounds(doc: SketchDoc): { min: Pt; max: Pt } | null {
  const pts: Pt[] = [...doc.runs.flatMap((r) => r.pts), ...doc.markers.map((m) => m.at)];
  if (!pts.length) return null;
  return {
    min: { x: Math.min(...pts.map((p) => p.x)), y: Math.min(...pts.map((p) => p.y)) },
    max: { x: Math.max(...pts.map((p) => p.x)), y: Math.max(...pts.map((p) => p.y)) },
  };
}

/** Distance from point p to segment ab, feet. */
export function distToSeg(p: Pt, a: Pt, b: Pt): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const l2 = vx * vx + vy * vy;
  if (l2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, { x: a.x + t * vx, y: a.y + t * vy });
}

export function hitRun(doc: SketchDoc, p: Pt, tol: number): SketchRun | null {
  let best: SketchRun | null = null;
  let bestD = tol;
  for (const run of doc.runs) {
    for (let i = 1; i < run.pts.length; i++) {
      const d = distToSeg(p, run.pts[i - 1], run.pts[i]);
      if (d < bestD) {
        bestD = d;
        best = run;
      }
    }
  }
  return best;
}

export function hitMarker(doc: SketchDoc, p: Pt, tol: number): SketchMarker | null {
  let best: SketchMarker | null = null;
  let bestD = tol;
  for (const m of doc.markers) {
    const d = dist(p, m.at);
    if (d < bestD) {
      bestD = d;
      best = m;
    }
  }
  return best;
}

export const nextMark = (prefix: string, used: string[]): string => {
  let n = 1;
  const taken = new Set(used);
  while (taken.has(`${prefix}-${String(n).padStart(2, "0")}`)) n++;
  return `${prefix}-${String(n).padStart(2, "0")}`;
};

/** Roll takeoff rows up by qtyKey. */
export function mergeLines(lines: TakeoffLine[]): TakeoffLine[] {
  const map = new Map<string, TakeoffLine>();
  for (const l of lines) {
    const prev = map.get(l.qtyKey);
    if (prev) {
      prev.qty += l.qty;
      prev.hours += l.hours;
      prev.from = Array.from(new Set([...(prev.from ?? []), ...(l.from ?? [])]));
      if (l.warn && !prev.warn) prev.warn = l.warn;
    } else {
      map.set(l.qtyKey, { ...l, from: [...(l.from ?? [])] });
    }
  }
  return Array.from(map.values()).filter((l) => l.qty > 0.0001);
}
